import { StatusTypes } from 'module/api/integrations/salesforce/types';
import { IntegrationField } from 'module/api/integrations/salesforce/types';
import { defaultFilter, Filter } from 'module/types/filter-field/filter';
import { dateFilterOperationOptions } from 'module/types/filter-field/filter/date';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
  DrawerBreadcrumb,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Loading } from 'module/ui/components/atoms/loading';
import { FilterBox } from 'module/ui/components/molecules/field-filter/popover/filter-popover/filter-box';
import { Button } from '@rocketlane/ui';
import { FC, ReactNode, useMemo, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { convertIntegrationFieldToField } from 'components/pages/integrations/salesforce/utils/field-conversion';

interface Props {
  visible: boolean;
  onClose: VoidFunction;
  title: ReactNode;
  breadcrumbs: DrawerBreadcrumb[];
  apiStatus: StatusTypes | boolean;
  value;
  fields: IntegrationField[];
  onSubmit: (triggers: Filter) => void;
}

const getSafeFilter = (value: unknown): Filter => {
  if (
    value &&
    typeof value === 'object' &&
    Array.isArray((value as Filter).criteria)
  ) {
    return value as Filter;
  }

  return defaultFilter;
};

export const TriggerSelectionDrawer: FC<Props> = ({
  visible,
  onClose,
  title,
  breadcrumbs,
  apiStatus,
  fields,
  value,
  onSubmit,
}) => {
  const [triggers, setTriggers] = useState<Filter>(getSafeFilter(value));
  const transformedFields = useMemo(
    () => convertIntegrationFieldToField(fields),
    [fields],
  );

  // Sync local state when value prop changes (for edit mode)
  useEffect(() => {
    if (visible) {
      setTriggers(getSafeFilter(value));
    }
  }, [value, visible]);

  return (
    <CustomLayoutDrawer
      {...DefaultCustomLayoutDrawerProps}
      open={visible}
      width={880}
      onClose={onClose}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title
          breadcrumbs={<DrawerBreadCrumbs breadcrumbs={breadcrumbs} />}
        >
          {title}
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          {apiStatus === 'pending' || apiStatus === true ? (
            <Loading />
          ) : (
            <FilterBox
              showFilterType={false}
              fields={transformedFields}
              filter={triggers}
              onChange={setTriggers}
              getFilterOperationTypeOptions={{
                Date: dateFilterOperationOptions,
              }}
            />
          )}
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer $rightAlignedAction>
          <Button variant="secondary" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onSubmit(triggers);
              onClose();
            }}
          >
            <FormattedMessage id="done" />
          </Button>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
